import { FC, useState, useEffect, useRef } from 'react';
import { CatchRegistrationCatchWrapper } from './CatchRegistrationCatch.styled';
import { Checkbox, FormControl, InputAdornment, InputLabel, OutlinedInput, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import PostCatchBody from '../../../../../Models/CatchRegistrationModels/PostCatchBody';
import CatchRegistration from '../../../../../Models/CatchRegistrationModels/CatchRegistration';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SetMealIcon from '@mui/icons-material/SetMeal';
import SignLanguageIcon from '@mui/icons-material/SignLanguage';
import CatchCombination from '../../../../../Models/CatchRegistrationModels/CatchCombination';
import FishingArea from '../../../../../Models/CatchRegistrationModels/FishingArea';
import StorageMethod from '../../../../../Models/CatchRegistrationModels/StorageMethod';
import Destiny from '../../../../../Models/CatchRegistrationModels/Destiny';
import FishingStock from '../../../../../Models/CatchRegistrationModels/FishingStock';
import Condition from '../../../../../Models/CatchRegistrationModels/Condition';
import WeightNote from '../../../../../Models/WeightNoteModels/WeightNote';
import Gear from '../../../../../Models/WeightNoteModels/Gear';
import WeightNoteTypes from '../../../../../Constants/WeightNoteTypes';
import CatchRegistrationDefaults from '../../../../../Constants/CatchRegistrationDefaults';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';

interface CatchRegistrationCatchProps {
  handleFormChange: (updatedForm: PostCatchBody) => void;
  formData: PostCatchBody;
  stockInputRef?: React.RefObject<HTMLInputElement>;
  stockFocusTrigger?: number;
  layout?: 'compact';
  allowedCombinations: CatchCombination[] | null;
  gears?: Gear[];
  weight: CatchRegistration | undefined;
  selectedWeightNote: Pick<WeightNote, 'weights' | 'weightNoteType' | 'isClosed' | 'gear'>;
  fishAreas: FishingArea[];
  storageMethods: StorageMethod[];
  destinies: Destiny[];
  fishingStocks: FishingStock[];
  conditions: Condition[];
  disabled: boolean;
  containsExported: boolean;
  onSkipTab?: () => void;
}

/**
 * Functional component for CatchRegistrationCatch.
 * Displays the catch form for the catch registration modal.
 * @param props 
 * - takes in various values used for dropDown values.
 * - takes in a function to be called when the form changes.
 * - takes in a boolean to determine if the weight note is closed.
 * - takes in a CatchRegistration object to determine if the modal is in edit state.
 * - takes in a CatchCombination array to determine which combinations are allowed (this component handles filtering of allowed catch based on changes).
 * 
 * @returns {JSX} - Responsible for returning the catch form.
 */

const CatchRegistrationCatch: FC<CatchRegistrationCatchProps> = (props) => {
  const [fishingAreaInputValue, setFishingAreaInputValue] = useState<string>('');
  const [storageMethodInputValue, setStorageMethodInputValue] = useState<string>('');
  const [destinyInputValue, setDestinyInputValue] = useState<string>('');
  const [stockInputValue, setStockInputValue] = useState<string>('');
  const [conditionInputValue, setConditionInputValue] = useState<string>('');
  const [isUnderKilogramDisabled, setIsUnderKilogramDisabled] = useState<boolean>();
  const internalStockInputRef = useRef<HTMLInputElement | null>(null);
  const stockInputRef = props.stockInputRef ?? internalStockInputRef;
  const isUnderKilogramRef = useRef<HTMLInputElement | null>(null);

  const formData = props.formData ?? {};
  const hasFormValue = <K extends keyof PostCatchBody>(key: K) =>
    Object.prototype.hasOwnProperty.call(formData, key);
  const getFormValue = <K extends keyof PostCatchBody>(key: K) =>
    hasFormValue(key) ? formData[key] : undefined;

  // Default Autocomplete values
  const defaultFishingArea = props.selectedWeightNote.weights?.find(
    (weight) => weight.fishingArea.id !== CatchRegistrationDefaults.FISHING_AREA_ICELAND_ID)?.fishingArea
    || props.fishAreas.find((area) => area.id === CatchRegistrationDefaults.FISHING_AREA_ICELAND_ID);
  const defaultStorageMethod = (
    props.selectedWeightNote.weightNoteType?.id === WeightNoteTypes.PRODUCT_ID ||
    props.selectedWeightNote.weightNoteType?.id === WeightNoteTypes.PRODUCT_SAMPLE_ID) ?
    props.storageMethods.find((method) => method.id === CatchRegistrationDefaults.STORAGE_METHOD_FROZEN_ID) :
    props.storageMethods.find((method) => method.id === CatchRegistrationDefaults.STORAGE_METHOD_ICED_ID);
  const defaultDestiny = props.containsExported ?
    props.destinies.find((dest) => dest.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID) :
    props.destinies.find((dest) => dest.id === CatchRegistrationDefaults.DESTINY_PROCESSING_ID);
  const maxWeight = props.selectedWeightNote?.weights?.reduce((max, weight) =>
    weight.id > max.id ? weight : max, props.selectedWeightNote.weights[0]);
  // No damaged destiny allowed to stick between registrations (VS-afli)
  const lastDestiny = props.destinies.find(
    (dest) =>
      dest.id === maxWeight?.destiny.id &&
      dest.id !== CatchRegistrationDefaults.DESTINY_DAMAGED_ID &&
      dest.id !== CatchRegistrationDefaults.DESTINY_STRANDVEIDI_ID
  );

  const formIsUnderKilogram = getFormValue('isUnderKilogram');
  const isUnderKilogram = formIsUnderKilogram !== undefined
    ? Boolean(formIsUnderKilogram)
    : props.weight?.underKilogram ?? false;

  
  const formFishingAreaId = getFormValue('fishingAreaId');
  const selectedFishingAreaId = formFishingAreaId !== undefined
    ? formFishingAreaId
    : props.weight
      ? props.weight?.fishingArea?.id
      : defaultFishingArea?.id;
  const selectedFishingArea = props.fishAreas.find((area) => area.id === selectedFishingAreaId);

  const formStorageMethodId = getFormValue('storageMethodId');
  const selectedStorageMethodId = formStorageMethodId !== undefined
    ? formStorageMethodId
    : props.weight
      ? props.weight?.storageMethod?.id
      : defaultStorageMethod?.id;
  const selectedStorageMethod = props.storageMethods.find((method) => method.id === selectedStorageMethodId);

  const formDestinyId = getFormValue('destinyId');
  const creationDefaultDestiny = lastDestiny ? lastDestiny.id : defaultDestiny?.id;
  const selectedDestinyId = formDestinyId !== undefined
    ? formDestinyId
    : props.weight
      ? props.weight?.destiny?.id
      : creationDefaultDestiny;
  const selectedDestiny = props.destinies.find((dest) => dest.id === selectedDestinyId);

  const formFishingStockId = getFormValue('fishStockId');
  const selectedStockId = formFishingStockId !== undefined
    ? formFishingStockId
    : props.weight
      ? props.weight?.fishingStock?.id
      : undefined;
  const selectedStock = props.fishingStocks.find((stock) => stock.id === selectedStockId);

  const formConditionId = getFormValue('conditionId');
  const selectedConditionId = formConditionId !== undefined
    ? formConditionId
    : props.weight
      ? props.weight?.condition?.id
      : undefined;
  const selectedCondition = props.conditions.find((condition) => condition.id === selectedConditionId);
  

  useEffect(() => {
    if (!props.allowedCombinations) {
      setIsUnderKilogramDisabled(undefined);
      return;
    }
    const filteredCombinations = props.allowedCombinations.filter((combination) => {
      return (
        (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
        (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
        (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
        (!selectedStock || combination.fishStockId === selectedStock.id) &&
        (!selectedCondition || combination.conditionId === selectedCondition.id)
      );
    });
    const isDisabled = filteredCombinations.length > 0 && filteredCombinations.every((combination) => combination.isUnderKilogram === false);
    setIsUnderKilogramDisabled(isDisabled);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.allowedCombinations,
    selectedFishingArea?.id,
    selectedStorageMethod?.id,
    selectedDestiny?.id,
    selectedStock?.id,
    selectedCondition?.id,
    isUnderKilogram
  ]);

  useEffect(() => {
    if (props.weight) {
      return;
    }
    const updates: PostCatchBody = {};
    if (!hasFormValue('fishingAreaId') && defaultFishingArea?.id) {
      updates.fishingAreaId = defaultFishingArea.id;
    }
    if (!hasFormValue('storageMethodId') && defaultStorageMethod?.id) {
      updates.storageMethodId = defaultStorageMethod.id;
    }
    if (!hasFormValue('destinyId') && creationDefaultDestiny) {
      updates.destinyId = creationDefaultDestiny;
    }
    if (Object.keys(updates).length > 0) {
      props.handleFormChange(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDestiny, defaultFishingArea, defaultStorageMethod, props.weight, creationDefaultDestiny, formData]);

  const checkboxDisabled =
  props.selectedWeightNote.isClosed ||
  (!isUnderKilogram && isUnderKilogramDisabled);
  
  const handleCheckBoxClick = () => {
    if (checkboxDisabled) {
      return;
    }
    props.handleFormChange({ isUnderKilogram: !isUnderKilogram });
  };

  const handleFishingAreaChange = (selectedValue: FishingArea | undefined) => {
    const updatedForm: PostCatchBody = { fishingAreaId: selectedValue?.id ?? null };
    props.handleFormChange(updatedForm);
  };

  const handleStorageMethodChange = (selectedValue: StorageMethod | undefined) => {
    const updatedForm: PostCatchBody = { storageMethodId: selectedValue?.id ?? null };
    props.handleFormChange(updatedForm);
  };

  const handleDestinyChange = (selectedValue: Destiny | undefined) => {
    const updatedForm: PostCatchBody = { destinyId: selectedValue?.id ?? null };
    props.handleFormChange(updatedForm);
  };

  const handleFishingStockChange = (selectedValue: FishingStock | undefined) => {
    // Check if the previous Autocomplete components have values
    if (selectedFishingArea && selectedStorageMethod && selectedDestiny && selectedValue) {
      // Check if there is only one allowed combination
      const filteredCombinations = props.allowedCombinations?.filter((combination) => {
        return (
          combination.fishingAreaId === selectedFishingArea.id &&
          combination.storageMethodId === selectedStorageMethod.id &&
          combination.destinyId === selectedDestiny.id &&
          combination.fishStockId === selectedValue.id
        );
      });

      if (filteredCombinations?.length === 1) {
        const allowedCombination = filteredCombinations[0];
        const condition = props.conditions.find((condition) => condition.id === allowedCombination.conditionId);
        if (condition) {
          handleConditionChange(condition);
          props.onSkipTab?.();
        }
      }
    }
    const updatedForm: PostCatchBody = {
      fishStockId: selectedValue?.id ?? null
    };
    props.handleFormChange(updatedForm);
  };

  const handleConditionChange = (selectedValue: Condition | undefined) => {
    const updatedForm: PostCatchBody = {
      conditionId: selectedValue?.id ?? null
    };
    props.handleFormChange(updatedForm);
    // We now must decide based on the change if isUnderKiloGram will become disabled and skip the focus if so.
    const filteredCombinations = props.allowedCombinations?.filter((combination) => {
      return (
        (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
        (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
        (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
        (!selectedStock || combination.fishStockId === selectedStock.id) &&
        (!selectedValue || combination.conditionId === selectedValue.id)
      );
    });
    const isUnderKilogramDisabled = filteredCombinations?.every((combination) => combination.isUnderKilogram === false);
    if (isUnderKilogramDisabled) {
      props.onSkipTab?.();
    }
  };

  const handleInputFishingAreaChange = (value: string) => {
    setFishingAreaInputValue(value);
    if (value === '') {
      const updatedForm: PostCatchBody = {
        fishingAreaId: null
      };
      props.handleFormChange(updatedForm);
    }
  };

  const handleInputDestinyChange = (value: string) => {
    setDestinyInputValue(value);
    if (value === '') {
      const updatedForm: PostCatchBody = {
        destinyId: null
      };
      props.handleFormChange(updatedForm);
    }
  };

  const handleInputStorageMethodChange = (value: string) => {
    setStorageMethodInputValue(value);
    if (value === '') {
      const updatedForm: PostCatchBody = {
        storageMethodId: null
      };
      props.handleFormChange(updatedForm);
    }
  };

  const handleInputFishingStockChange = (value: string) => {
    setStockInputValue(value);
    if (value === '') {
      const updatedForm: PostCatchBody = {
        fishStockId: null
      };
      props.handleFormChange(updatedForm);
    }
  };

  const handleInputConditionChange = (value: string) => {
    setConditionInputValue(value);
    if (value === '') {
      const updatedForm: PostCatchBody = {
        conditionId: null
      };
      props.handleFormChange(updatedForm);
    }
  };

  useEffect(() => {
    if (!props.weight && (props.stockFocusTrigger ?? 0) > 0) {
      requestAnimationFrame(() => stockInputRef.current?.focus());
    }
  }, [props.stockFocusTrigger, props.weight, stockInputRef]);

  const filterAndSortOptions = (options: FishingStock[], inputValue: string) => {
    // Normalize the input value to handle Icelandic characters.
    // In old GAFL users were custom to input "o" and get "ó" options.
    // This hacky regex removes all accents from characters.
    // Normalize input to handle Icelandic characters
    const normalize = (text: string) =>
      text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const inputValueNormalized = normalize(inputValue);

    // Items that start with the input
    const matchingStart = options.filter(option =>
      normalize(option.name).startsWith(inputValueNormalized)
    );

    // Items that contain the input anywhere
    const matchingIncludes = options.filter(option =>
      normalize(option.name).includes(inputValueNormalized)
    );

    // Remove duplicates from includes (avoid re-adding start matches)
    const uniqueIncludes = matchingIncludes.filter(
      include => !matchingStart.includes(include)
    );

    // Sort start matches by usage (only if same starting group)
    // User were often inputing "gu" which gave "gulllax" the benefit over "gullkarfi" for example.
    // By using weighted search we hopefully fix this forever and for other cases as well.
    matchingStart.sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0));

    // Sort includes by usage as well
    uniqueIncludes.sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0));

    // Return starts first, then includes
    return [...matchingStart, ...uniqueIncludes];
  };

  function filterStocksBasedOnGear() {
    return props.fishingStocks.filter(stock =>
      (props.gears?.find(gear => gear.id === props.selectedWeightNote.gear.id)?.forbiddenStocks || [])
        .every(forbiddenStock => forbiddenStock.id !== stock.id)
    );
  }

  const filterOptions = (optionName: 'fishingArea' | 'destiny' | 'storageMethod' | 'fishingStock' | 'condition') => {
    // Filter options based on other selected values
    if (optionName === 'fishingArea') {
      const matchingFishingAreas: FishingArea[] = [];
      const fishingAreaIds: Set<number> = new Set();
      props.allowedCombinations?.forEach((combination) => {
        if (
          (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
          (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
          (!selectedStock || combination.fishStockId === selectedStock.id) &&
          (!selectedCondition || combination.conditionId === selectedCondition.id) &&
          (!isUnderKilogram || combination.isUnderKilogram === isUnderKilogram)
        ) {
          const fishingAreaId = combination.fishingAreaId;
          if (!fishingAreaIds.has(fishingAreaId)) {
            fishingAreaIds.add(fishingAreaId);
            const fishingArea = props.fishAreas.find((area) => area.id === fishingAreaId);
            if (fishingArea) {
              matchingFishingAreas.push(fishingArea);
            }
          }
        }
      });
      return matchingFishingAreas;
    }
    if (optionName === 'destiny') {
      const matchingDestinies: Destiny[] = [];
      const destinyIds: Set<number> = new Set();
      props.allowedCombinations?.forEach((combination) => {
        if (
          (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
          (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
          (!selectedStock || combination.fishStockId === selectedStock.id) &&
          (!selectedCondition || combination.conditionId === selectedCondition.id) &&
          (!isUnderKilogram || combination.isUnderKilogram === isUnderKilogram)
        ) {
          const destinyId = combination.destinyId;
          if (!destinyIds.has(destinyId)) {
            destinyIds.add(destinyId);
            const destiny = props.destinies.find((dest) => dest.id === destinyId);
            if (destiny) {
              matchingDestinies.push(destiny);
            }
          }
        }
      });
      return matchingDestinies;
    }
    if (optionName === 'storageMethod') {
      const matchingStorageMethods: StorageMethod[] = [];
      const storageMethodIds: Set<number> = new Set();
      props.allowedCombinations?.forEach((combination) => {
        if (
          (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
          (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
          (!selectedStock || combination.fishStockId === selectedStock.id) &&
          (!selectedCondition || combination.conditionId === selectedCondition.id) &&
          (!isUnderKilogram || combination.isUnderKilogram === isUnderKilogram)
        ) {
          const storageMethodId = combination.storageMethodId;
          if (!storageMethodIds.has(storageMethodId)) {
            storageMethodIds.add(storageMethodId);
            const storageMethod = props.storageMethods.find((method) => method.id === storageMethodId);
            if (storageMethod) {
              matchingStorageMethods.push(storageMethod);
            }
          }
        }
      });
      return matchingStorageMethods;
    }
    if (optionName === 'fishingStock') {
      const matchingFishingStocks: FishingStock[] = [];
      const fishingStockIds: Set<number> = new Set();
      props.allowedCombinations?.forEach((combination) => {
        if (
          (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
          (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
          (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
          (!selectedCondition || combination.conditionId === selectedCondition.id) &&
          (!isUnderKilogram || combination.isUnderKilogram === isUnderKilogram)
        ) {
          const fishingStockId = combination.fishStockId;
          if (!fishingStockIds.has(fishingStockId)) {
            fishingStockIds.add(fishingStockId);
            const fishingStock = filterStocksBasedOnGear().find((stock) => stock.id === fishingStockId);
            if (fishingStock) {
              matchingFishingStocks.push(fishingStock);
            }
          }
        }
      });
      return matchingFishingStocks;
    }
    if (optionName === 'condition') {
      const matchingConditions: Condition[] = [];
      const conditionIds: Set<number> = new Set();
      props.allowedCombinations?.forEach((combination) => {
        if (
          (!selectedFishingArea || combination.fishingAreaId === selectedFishingArea.id) &&
          (!selectedStorageMethod || combination.storageMethodId === selectedStorageMethod.id) &&
          (!selectedDestiny || combination.destinyId === selectedDestiny.id) &&
          (!selectedStock || combination.fishStockId === selectedStock.id) &&
          (!isUnderKilogram || combination.isUnderKilogram === isUnderKilogram)
        ) {
          const conditionId = combination.conditionId;
          if (!conditionIds.has(conditionId)) {
            conditionIds.add(conditionId);
            const condition = props.conditions.find((cond) => cond.id === conditionId);
            if (condition) {
              matchingConditions.push(condition);
            }
          }
        }
      });
      return matchingConditions;
    }
    return [];
  };

  const stockField = (
    <Autocomplete
      sx={{ width: '100%' }}
      disablePortal
      autoSelect={stockInputValue !== ''}
      disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
      autoHighlight
      options={filterOptions('fishingStock')}
      filterOptions={(options, state) => filterAndSortOptions(options, state.inputValue)}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      value={selectedStock || null}
      onChange={(event, value) => handleFishingStockChange(value || undefined)}
      onInputChange={(event, value) => handleInputFishingStockChange(value)}
      slotProps={{
        popper: {
          disablePortal: false,
        }
      }}
      renderInput={(params) =>
        <TextField
          inputRef={stockInputRef}
          label="Veiðistofn"
          color="primary"
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SetMealIcon color='primary' />
              </InputAdornment>
            )
          }}
        />}
    />
  );
  const conditionField = (
    <Autocomplete
      sx={{ width: '100%' }}
      disablePortal
      disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
      autoHighlight
      autoSelect={conditionInputValue !== ''}
      options={filterOptions('condition')}
      filterOptions={(options, state) => filterAndSortOptions(options, state.inputValue)}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      value={selectedCondition || null}
      onChange={(event, value) => handleConditionChange(value || undefined)}
      onInputChange={(event, value) => handleInputConditionChange(value)}
      slotProps={{
        popper: {
          disablePortal: false,
        }
      }}
      renderInput={(params) =>
        <TextField
          label="Ástand"
          color="primary"
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SignLanguageIcon color='primary' />
              </InputAdornment>
            )
          }}
        />}
    />
  );

  return (
    <CatchRegistrationCatchWrapper $compact={props.layout === 'compact'}>
      {props.layout === 'compact' && <>{stockField}{conditionField}</>}
      <Autocomplete
        sx={{ width: '100%' }}
        disablePortal
        disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
        options={filterOptions('fishingArea')}
        filterOptions={(options, state) => filterAndSortOptions(options, state.inputValue)}
        autoHighlight
        autoSelect={fishingAreaInputValue !== ''}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={selectedFishingArea || null}
        onChange={(event, value) => handleFishingAreaChange(value || undefined)}
        onInputChange={(event, value) => handleInputFishingAreaChange(value)}
        slotProps={{
          popper: {
            disablePortal: false,
          }
        }}
        renderInput={(params) =>
          <TextField
            label="Veiðisvæði"
            color="primary"
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon color='primary' />
                </InputAdornment>
              )
            }}
          />}
      />
      <Autocomplete
        sx={{ width: '100%' }}
        disablePortal
        disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
        autoHighlight
        autoSelect={storageMethodInputValue !== ''}
        options={filterOptions('storageMethod')}
        filterOptions={(options, state) => filterAndSortOptions(options, state.inputValue)}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={selectedStorageMethod || null}
        onChange={(event, value) => handleStorageMethodChange(value || undefined)}
        onInputChange={(event, value) => handleInputStorageMethodChange(value)}
        slotProps={{
          popper: {
            disablePortal: false,
          }
        }}
        renderInput={(params) =>
          <TextField
            label="Geymsluaðferð"
            color="primary"
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <AcUnitIcon color='primary' />
                </InputAdornment>
              )
            }}
          />}
      />

      <Autocomplete
        sx={{ width: '100%' }}
        disablePortal
        disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
        autoHighlight
        autoSelect={destinyInputValue !== ''}
        options={filterOptions('destiny')}
        filterOptions={(options, state) => filterAndSortOptions(options, state.inputValue)}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={selectedDestiny || null}
        onChange={(event, value) => handleDestinyChange(value || undefined)}
        onInputChange={(event, value) => handleInputDestinyChange(value)}
        slotProps={{
          popper: {
            disablePortal: false,
          }
        }}
        renderInput={(params) =>
          <TextField
            label="Afdrif"
            color="primary"
            helperText={(props.containsExported && !props?.selectedWeightNote?.isClosed) ? "Aflaskráning merkt 'Útflutningur' fannst á vigtarnótu." : ""}
            error={props.containsExported && selectedDestiny?.id !== CatchRegistrationDefaults.DESTINY_EXPORTED_ID}
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <ImportExportIcon color='primary' />
                </InputAdornment>
              )
            }}
          />}
      />

      {props.layout !== 'compact' && stockField}

      {props.layout !== 'compact' && conditionField}

      <FormControl sx={{ width: '100%' }} variant="outlined">
        <InputLabel shrink id="under-one-kg">
    Undirmál
        </InputLabel>
        <OutlinedInput
          notched
          ref={isUnderKilogramRef}
          id="under-one-kg-checkbox"
          label="Undirmál"
          inputProps={{
            readOnly: true
          }}
          sx={{
            cursor: checkboxDisabled ? 'not-allowed' : 'pointer',
            '&:hover': {
              backgroundColor: (theme) =>
                !checkboxDisabled ? theme.palette.action.hover : 'inherit',
            },
            '& input, & .MuiOutlinedInput-input': {
              cursor: 'inherit',
            },
          }}
          startAdornment={
            <InputAdornment position="start">
              <AlignVerticalBottomIcon color="primary" />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <Checkbox
                tabIndex={-1}
                disabled={checkboxDisabled}
                checked={isUnderKilogram}
                color="primary"
                sx={{
                  padding: 1,
                  cursor: checkboxDisabled ? 'not-allowed' : 'pointer',
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}
                inputProps={{ 'aria-label': 'Undirmál checkbox' }}
              />
            </InputAdornment>
          }
          disabled={checkboxDisabled}
          tabIndex={-1}
          onClick={() => {
            if (!checkboxDisabled) {
              handleCheckBoxClick();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (!checkboxDisabled) {
                handleCheckBoxClick();
              }
            }
          }}
          value={isUnderKilogram ? 'Já' : 'Nei'}
          aria-label="Undirmál"
          role="checkbox"
          aria-checked={isUnderKilogram}
        />
      </FormControl>


    </CatchRegistrationCatchWrapper>
  );
};

export default CatchRegistrationCatch;
